import { ProceduralPoolGrid } from '../../../../Pool/logic/ProceduralPoolGrid';
import { getDrainModelParams } from './positioning';
import { DRAIN_CONSTANTS } from './constants';

/**
 * Фиксированный отступ от стены (м), с ограничением на очень малых чашах.
 * @param {number} innerLength
 * @param {number} innerWidth
 * @returns {number}
 */
function resolveDrainWallInset(innerLength, innerWidth) {
    const requested = DRAIN_CONSTANTS.POSITIONING.WALL_INSET_M ?? 0.2;
    const maxInset = Math.min(innerWidth / 2, innerLength / 2) - 0.05;
    return Math.min(requested, Math.max(0.05, maxInset));
}

/**
 * Все донные сливы в один ряд вдоль одной стороны дна (скиммерный и переливной).
 * @param {Object} params
 * @returns {Array}
 */
function calculateSingleSideDrainPositions({
    innerLength,
    innerWidth,
    innerFloorY,
    heightCompensation,
    scale,
    totalQuantity,
    isLengthLonger,
    wallInset,
    drain,
}) {
    const instances = [];
    const y = innerFloorY + heightCompensation + DRAIN_CONSTANTS.POSITIONING.VERTICAL_OFFSET;

    for (let i = 0; i < totalQuantity; i++) {
        let x = 0;
        let z = 0;
        let rotation = [0, 0, 0];

        // Ряд сливов идёт вдоль КОРОТКОЙ стороны (не длинной) — поэтому
        // условие инвертировано относительно isLengthLonger.
        if (!isLengthLonger) {
            x = innerWidth / 2 - wallInset;
            if (totalQuantity === 1) {
                z = 0;
            } else {
                const step = innerLength / (totalQuantity + 1);
                z = -innerLength / 2 + step * (i + 1);
            }
            rotation = [0, -Math.PI / 2, 0];
        } else {
            z = -(innerLength / 2) + wallInset;
            if (totalQuantity === 1) {
                x = 0;
            } else {
                const step = innerWidth / (totalQuantity + 1);
                x = -innerWidth / 2 + step * (i + 1);
            }
            rotation = [0, Math.PI, 0];
        }

        instances.push({
            position: [x, y, z],
            rotation,
            scale,
            drain,
            index: i,
            groupType: 'bottom',
        });
    }

    return instances;
}

/**
 * Расчет позиций донных сливов
 * @param {Object} poolDimensions - { length, width, depth, type? }
 * @param {number} quantity
 * @param {string} poolType
 * @param {Object} drain - данные слива
 * @returns {Array}
 */
export const calculateDrainPositions = (poolDimensions, quantity = 1, poolType, drain) => {
    if (!poolDimensions || quantity <= 0) return [];

    const { length, width, depth } = poolDimensions;
    const resolvedPoolType = poolType ?? poolDimensions.type;
    const gridSystem = new ProceduralPoolGrid(length, width, depth, resolvedPoolType);
    const { innerLength, innerWidth, floorY: innerFloorY } = gridSystem.dims;

    const { scale, heightCompensation } = getDrainModelParams(depth);

    const isLengthLonger = length >= width;
    const wallInset = resolveDrainWallInset(innerLength, innerWidth);
    const totalQuantity = quantity;

    return calculateSingleSideDrainPositions({
        innerLength,
        innerWidth,
        innerFloorY,
        heightCompensation,
        scale,
        totalQuantity,
        isLengthLonger,
        wallInset,
        drain,
    });
};
